/**
 * Navigation graph.
 *
 * A drawer wrapping a stack: the drawer is the primary navigation, the stack
 * carries the checkout flow. No bottom tab bar, per the phone UX rules.
 */

import { Dimensions } from 'react-native';
import { NavigationContainer, createNavigationContainerRef } from '@react-navigation/native';
import { createDrawerNavigator } from '@react-navigation/drawer';
import { createNativeStackNavigator } from '@react-navigation/native-stack';

import { colors, sizing } from '../../theme';
import { DrawerContent } from './DrawerContent';
import { useLanguage } from '../../core/i18n/LanguageProvider';
import { DistributionProofScreen } from '../../features/donations/DistributionProofScreen';
import { DonateMealScreen } from '../../features/donation/DonateMealScreen';
import { PeopleCountScreen } from '../../features/donation/PeopleCountScreen';
import { DonorInformationScreen } from '../../features/checkout/DonorInformationScreen';
import { RecipientInformationScreen } from '../../features/checkout/RecipientInformationScreen';
import { ContactScreen } from '../../features/contact/ContactScreen';
import { HelpScreen } from '../../features/support/HelpScreen';
import { HomeScreen } from '../../features/home/HomeScreen';
import { HowItWorksScreen } from '../../features/howItWorks/HowItWorksScreen';
import { ImpactScreen } from '../../features/impact/ImpactScreen';
import { MyOrdersScreen } from '../../features/orders/MyOrdersScreen';
import { NotificationsScreen } from '../../features/notifications/NotificationsScreen';
import { OrderDetailsScreen } from '../../features/checkout/OrderDetailsScreen';
import { PaymentScreen } from '../../features/checkout/PaymentScreen';
import { ProfileScreen } from '../../features/profile/ProfileScreen';
import { ReceiptScreen } from '../../features/receipt/ReceiptScreen';
import { ReviewScreen } from '../../features/checkout/ReviewScreen';
import { ThankYouScreen } from '../../features/checkout/ThankYouScreen';
import { PaymentPendingScreen } from '../../features/checkout/PaymentPendingScreen';
import { TrackingScreen } from '../../features/donations/TrackingScreen';
import { TiffinConfirmationScreen } from '../../features/tiffin/TiffinConfirmationScreen';
import { TiffinContactScreen } from '../../features/tiffin/TiffinContactScreen';
import { TiffinDeliveryScreen } from '../../features/tiffin/TiffinDeliveryScreen';
import { TiffinMenuScreen } from '../../features/tiffin/TiffinMenuScreen';
import { TiffinOrderScreen } from '../../features/tiffin/TiffinOrderScreen';
import { TiffinPaymentScreen } from '../../features/tiffin/TiffinPaymentScreen';
import { TiffinPersonsScreen } from '../../features/tiffin/TiffinPersonsScreen';
import { TiffinReviewScreen } from '../../features/tiffin/TiffinReviewScreen';
import type { DonorDrawerParamList, DonorStackParamList } from './types';
import { consumePaymentReturn } from '../../core/payments/hostedPage';

const Stack = createNativeStackNavigator<DonorStackParamList>();
const Drawer = createDrawerNavigator<DonorDrawerParamList>();

function MainStack() {
  return (
    <Stack.Navigator
      initialRouteName="Home"
      // Every screen draws its own header through ScreenShell.
      screenOptions={{ headerShown: false, contentStyle: { backgroundColor: colors.bg.page } }}
    >
      <Stack.Screen name="Home" component={HomeScreen} />
      <Stack.Screen name="HowItWorks" component={HowItWorksScreen} />
      <Stack.Screen name="DonateMeal" component={DonateMealScreen} />
      <Stack.Screen name="PeopleCount" component={PeopleCountScreen} />
      <Stack.Screen name="OrderDetails" component={OrderDetailsScreen} />
      <Stack.Screen name="DonorInformation" component={DonorInformationScreen} />
      <Stack.Screen name="RecipientInformation" component={RecipientInformationScreen} />
      <Stack.Screen name="Payment" component={PaymentScreen} />
      <Stack.Screen name="Review" component={ReviewScreen} />
      <Stack.Screen name="PaymentPending" component={PaymentPendingScreen} />
      <Stack.Screen
        name="ThankYou"
        component={ThankYouScreen}
        // Confirmation is a dead end for the back gesture: swiping back into
        // the checkout after paying would invite a second donation.
        options={{ gestureEnabled: false }}
      />
      <Stack.Screen name="TiffinMenu" component={TiffinMenuScreen} />
      <Stack.Screen name="TiffinPersons" component={TiffinPersonsScreen} />
      <Stack.Screen name="TiffinContact" component={TiffinContactScreen} />
      <Stack.Screen name="TiffinDelivery" component={TiffinDeliveryScreen} />
      <Stack.Screen name="TiffinPayment" component={TiffinPaymentScreen} />
      <Stack.Screen name="TiffinReview" component={TiffinReviewScreen} />
      <Stack.Screen
        name="TiffinConfirmation"
        component={TiffinConfirmationScreen}
        // As with ThankYou: no swiping back into a checkout that has been paid.
        options={{ gestureEnabled: false }}
      />
      <Stack.Screen name="TiffinOrder" component={TiffinOrderScreen} />
      <Stack.Screen name="MyOrders" component={MyOrdersScreen} />
      <Stack.Screen name="Tracking" component={TrackingScreen} />
      <Stack.Screen name="DistributionProof" component={DistributionProofScreen} />
      <Stack.Screen name="Receipt" component={ReceiptScreen} />
      <Stack.Screen name="Impact" component={ImpactScreen} />
      <Stack.Screen name="Notifications" component={NotificationsScreen} />
      <Stack.Screen name="Profile" component={ProfileScreen} />
      <Stack.Screen name="Help" component={HelpScreen} />
      <Stack.Screen name="Contact" component={ContactScreen} />
    </Stack.Navigator>
  );
}

const navigationRef = createNavigationContainerRef();

export function RootNavigator() {
  const { isRTL } = useLanguage();
  const width = Math.min(
    Dimensions.get('window').width * sizing.drawerWidthRatio,
    sizing.drawerMaxWidth,
  );

  return (
    <NavigationContainer
      ref={navigationRef}
      onReady={() => {
        // Back from Stripe or JazzCash on the website: reopen the waiting
        // screen for that payment rather than dropping the donor on Home.
        const back = consumePaymentReturn();
        if (back) {
          // The drawer's single "Main" stack hosts every screen.
          (navigationRef.navigate as (name: string, params: object) => void)('Main', {
            screen: 'PaymentPending',
            params: { paymentId: back.paymentId, returned: true, cancelled: back.cancelled },
          });
        }
      }}
    >
      <Drawer.Navigator
        drawerContent={(props) => <DrawerContent {...props} />}
        screenOptions={{
          headerShown: false,
          drawerType: 'front',
          drawerPosition: isRTL ? 'right' : 'left',
          drawerStyle: { width, backgroundColor: colors.bg.surface },
          overlayColor: colors.bg.scrim,
          swipeEdgeWidth: 40,
        }}
      >
        <Drawer.Screen name="Main" component={MainStack} />
      </Drawer.Navigator>
    </NavigationContainer>
  );
}
